import { BusinessObjectBase } from './base';
import { userActivityPeriodSchema } from '@schemas/business-objects/user-activity-period';
import type { User } from './user';
import type { Id } from '../base-types';

export interface UserActivityPeriodData {
  readonly id: Id;
  readonly periodStart: Date;
  readonly requestCount?: number;
  readonly userId: Id;
  readonly user?: User;
}

export interface UserActivityPeriod extends Readonly<UserActivityPeriodData> {}
export class UserActivityPeriod extends BusinessObjectBase {
  static readonly entityName = 'UserActivityPeriod' as const;
  static readonly plural = 'user-activity-periods';
  static schema = userActivityPeriodSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<UserActivityPeriod[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new UserActivityPeriod(data));
  }

  static async findOne(filter?: any): Promise<UserActivityPeriod | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new UserActivityPeriod(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<UserActivityPeriod> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new UserActivityPeriod(data);
  }

  static async create(_data: any): Promise<UserActivityPeriod> {
    throw new Error('create is not available on UserActivityPeriod');
  }

  static async createMany(_data: any[]): Promise<UserActivityPeriod[]> {
    throw new Error('createMany is not available on UserActivityPeriod');
  }

  static async createWithChildren(_data: any): Promise<UserActivityPeriod> {
    throw new Error(
      'createWithChildren is not available on UserActivityPeriod',
    );
  }

  static async createManyWithChildren(
    _data: any[],
  ): Promise<UserActivityPeriod[]> {
    throw new Error(
      'createManyWithChildren is not available on UserActivityPeriod',
    );
  }

  static async updateById(_id: Id, _updates: any): Promise<UserActivityPeriod> {
    throw new Error('updateById is not available on UserActivityPeriod');
  }

  static async deleteById(_id: Id): Promise<boolean> {
    throw new Error('deleteById is not available on UserActivityPeriod');
  }

  // --- Behaviors ---

  static async sendDigest(daysAgo?: number): Promise<{
    day: string;
    users: number;
    activeMinutes: number;
    requests: number;
    sent: boolean;
  }> {
    const url = `${this.baseUrl}/${this.plural}/send-digest`;
    return this.post<{
      day: string;
      users: number;
      activeMinutes: number;
      requests: number;
      sent: boolean;
    }>(url, { daysAgo });
  }
}
